import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnDestroy,
  OnInit,
  signal,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
  ViewEncapsulation,
} from '@angular/core';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import {
  Notification,
  JobStatus,
  PanelView,
  RealtimeApprovalNotification,
} from 'app/layout/common/notifications/notifications.types';
import { NotificationsService } from 'app/layout/common/notifications/notifications.service';
import { Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BehaviorSubject, forkJoin, merge, switchMap } from 'rxjs';
import { PusherService } from '@core/services';
import { WINDOW } from 'app/providers/window.provider';
import { Store } from '@ngxs/store';
import { AccountState } from '@state/account/state';
import { UserState } from '@state/user/state';
import { FeatureFlag } from '@core/enums';
import { STATUS_BADGE } from '@modules/jupiter/@components/tabs/approvals/approvals.types';
import { ApprovalStatus } from '@shared/modules/approvals/approvals.types';
import { RelatedToType } from '@shared/enums';
import { ObjectsManagerState } from '@modules/settings/objects-manager/state/state';
import { APPROVAL_PROCESS_PUSHER_EVENTS } from '@shared/modules/approvals/approval-pusher.constants';
import { DEAL_CREATION_PUSHER_EVENTS } from '@modules/projects/@shared/constants/deal-creation.constants';

@Component({
  selector: 'notifications',
  templateUrl: './notifications.component.html',
  styleUrls: ['./notifications.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  exportAs: 'notifications',
})
export class NotificationsComponent implements OnInit, OnDestroy {
  private readonly notificationsService = inject(NotificationsService);
  private readonly overlay = inject(Overlay);
  private readonly router = inject(Router);
  private readonly viewContainerRef = inject(ViewContainerRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly pusherService = inject(PusherService);
  private readonly window = inject(WINDOW);
  private readonly store = inject(Store);

  private _overlayRef: OverlayRef;
  private loadData = new BehaviorSubject<void>(undefined);
  private previousNotificationIds = new Set<number>();
  private previousJobIds = new Set<number>();

  protected notifications = signal<Notification[]>([]);
  protected archivedNotifications = signal<Notification[]>([]);
  protected inProgressJobs = signal<JobStatus[]>([]);
  protected completedExports = signal<JobStatus[]>([]);
  protected unreadCount = signal(0);
  protected inProgressCount = signal(0);
  protected completedCount = signal(0);
  protected totalBadgeCount = signal(0);
  protected panelView = signal<PanelView>('main');
  protected showNewBadge = signal(false);
  protected realtimeNotifications = signal<RealtimeApprovalNotification[]>([]);
  protected readonly statusBadge = STATUS_BADGE;
  protected readonly statusIcon: Partial<Record<ApprovalStatus, string>> = {
    [ApprovalStatus.APPROVED]: 'check_circle',
    [ApprovalStatus.DECLINED]: 'cancel',
    [ApprovalStatus.NOT_YET]: 'schedule',
  };

  isNewNavigationOn$ = this.store.select(AccountState.isFeatureEnabled(FeatureFlag.NEW_NAVIGATION));

  @ViewChild('notificationsOrigin')
  private _notificationsOrigin: any;

  @ViewChild('notificationsPanel')
  private _notificationsPanel: TemplateRef<any>;

  ngOnInit(): void {
    const userId = this.store.selectSnapshot(UserState.getUser)?.id;

    this.loadData
      .pipe(
        switchMap(() =>
          forkJoin({
            notifications: this.notificationsService.getNotifications(),
            inProgressJobs: this.notificationsService.getInProgressJobs(userId),
            directDownloadNotifications: this.notificationsService.getDirectDownloadNotifications(userId),
          }),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(({ notifications: systemNotifications, inProgressJobs, directDownloadNotifications }) => {
        const notifications = [...systemNotifications, ...directDownloadNotifications];
        const unread = notifications.filter((n) => !n.is_read);
        const read = notifications.filter((n) => n.is_read);

        const sortedUnread = unread.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
        const sortedRead = read.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

        this.detectNewItems(sortedUnread, inProgressJobs);

        this.notifications.set(sortedUnread);
        this.archivedNotifications.set(sortedRead);
        this.inProgressJobs.set(inProgressJobs);
        this.unreadCount.set(sortedUnread.length);
        this.inProgressCount.set(inProgressJobs.length);
        this.completedCount.set(sortedUnread.length);
        this.totalBadgeCount.set(sortedUnread.length + inProgressJobs.length);
      });

    this.pusherService
      .listenToChannel({ channelName: PusherService.defaultChannelName, eventName: 'update' })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.loadData.next();
      });

    if (userId) {
      const channelName = `notifications-user-${userId}`;

      APPROVAL_PROCESS_PUSHER_EVENTS.forEach((eventName) => {
        this.pusherService.listenToChannel({ channelName, eventName });
      });

      // All channel events share one Subject; bind above then subscribe once.
      this.pusherService
        .listenToChannel({ channelName, eventName: APPROVAL_PROCESS_PUSHER_EVENTS[0] })
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((message: unknown) => {
          const notification = this.parseRealtimePayload(message);
          if (!notification) {
            return;
          }

          const hadExisting = this.realtimeNotifications().some((item) => item.key === notification.key);
          this.realtimeNotifications.update((items) => [
            notification,
            ...items.filter((item) => item.key !== notification.key),
          ]);

          if (!hadExisting) {
            this.totalBadgeCount.update((n) => n + 1);
          }
        });

      merge(
        ...DEAL_CREATION_PUSHER_EVENTS.map((eventName) => this.pusherService.listenToEvent({ channelName, eventName })),
      )
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => this.loadData.next());
    }

    this.notificationsService.refreshTrigger$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.loadData.next();
    });
  }

  ngOnDestroy(): void {
    if (this._overlayRef) {
      this._overlayRef.dispose();
    }
  }

  private parseRealtimePayload(message: any): RealtimeApprovalNotification | null {
    const approval = message?.approval;
    const approvalProcess = message?.approval_process;

    // review_requested / review_submitted both send { approval, approval_process }.
    // finished sends { approval_process } only.
    if (approval) {
      const processId = approval.approval_process_id ?? approvalProcess?.id;
      const relatedToType = approval.related_to_type ?? approvalProcess?.related_to_type;
      const relatedTo = approval.related_to ?? approvalProcess?.related_to;
      const { link, objectTypeLabel } = this.resolveApprovalContext(relatedToType, relatedTo, processId);
      if (!link) return null;

      const isDecision =
        !!approval.approved_at ||
        !!approval.declined_at ||
        approval.status === ApprovalStatus.APPROVED ||
        approval.status === ApprovalStatus.DECLINED;

      return {
        key: `approval-${approval.id}`,
        eventType: isDecision ? 'review_submitted' : 'review_requested',
        name: approval.name ?? approvalProcess?.name,
        relatedToTitle: relatedTo?.title ?? relatedTo?.name ?? '',
        objectTypeLabel,
        status: approval.status as ApprovalStatus,
        link,
        timestamp: approval.approved_at ?? approval.declined_at ?? new Date().toISOString(),
      };
    }

    if (approvalProcess) {
      const { id, status, name, completed_at, related_to_type, related_to } = approvalProcess;
      const { link, objectTypeLabel } = this.resolveApprovalContext(related_to_type, related_to, id);
      if (!link) return null;

      return {
        key: `approval_process-${id}`,
        eventType: 'process_finished',
        name,
        relatedToTitle: related_to?.title ?? related_to?.name ?? '',
        objectTypeLabel,
        status: status as ApprovalStatus,
        link,
        timestamp: completed_at ?? new Date().toISOString(),
      };
    }

    return null;
  }

  private resolveApprovalContext(
    relatedToType: string,
    relatedTo: any,
    approvalId: number,
  ): { link: string | null; objectTypeLabel: string | null } {
    const objectName =
      relatedToType === RelatedToType.CustomObjectValue ? relatedTo?.custom_object_name : relatedToType;
    const object = this.store.selectSnapshot(ObjectsManagerState.getByName(objectName));
    const objectTypeLabel = object?.label ?? null;

    if (relatedToType === RelatedToType.Project) {
      return {
        link: `/projects/${relatedTo?.pipeline_id}/${relatedTo?.id}/approvals/${approvalId}`,
        objectTypeLabel,
      };
    }

    if (!relatedTo?.id || !object?.uiConfig?.urlPrefix) {
      return { link: null, objectTypeLabel };
    }

    const deepLink = `/space/${object.uiConfig.urlPrefix}/0/${relatedTo.id}/approvals/${approvalId}`;
    return { link: `/space/void?return=${encodeURIComponent(deepLink)}`, objectTypeLabel };
  }

  protected dismissRealtimeNotification(key: string): void {
    this.realtimeNotifications.update((items) => items.filter((n) => n.key !== key));
    this.totalBadgeCount.update((n) => Math.max(0, n - 1));
  }

  protected navigateToApproval(notification: RealtimeApprovalNotification): void {
    this.router.navigateByUrl(notification.link);
    if (this._overlayRef?.hasAttached()) {
      this._overlayRef.detach();
    }
  }

  private detectNewItems(notifications: Notification[], jobs: JobStatus[]): void {
    const newNotificationIds = notifications.map((n) => n.id);
    const newJobIds = jobs.map((j) => j.id);

    const hasNewNotif = newNotificationIds.some((id) => !this.previousNotificationIds.has(id));
    const hasNewJob = newJobIds.some((id) => !this.previousJobIds.has(id));

    if ((hasNewNotif || hasNewJob) && (this.previousNotificationIds.size > 0 || this.previousJobIds.size > 0)) {
      this.showNewBadge.set(true);
      setTimeout(() => this.showNewBadge.set(false), 5000);
    }

    this.previousNotificationIds = new Set(newNotificationIds);
    this.previousJobIds = new Set(newJobIds);
  }

  protected isFailedNotification(notification: Notification): boolean {
    return (
      notification.subject?.toLowerCase().includes('failed') ||
      notification.body?.toLowerCase().includes('failed') ||
      false
    );
  }

  protected getJobTitle(job: JobStatus): string {
    return job.label || 'Processing';
  }

  protected getJobTime(job: JobStatus): string {
    return job.started_at ?? job.enqueued_at;
  }

  openPanel(): void {
    this.panelView.set('main');

    let element = this._notificationsOrigin.viewContainerRef.element.nativeElement;

    this._overlayRef = this.overlay.create({
      hasBackdrop: true,
      backdropClass: '',
      scrollStrategy: this.overlay.scrollStrategies.block(),
      positionStrategy: this.overlay
        .position()
        .flexibleConnectedTo(element)
        .withFlexibleDimensions()
        .withViewportMargin(16)
        .withLockedPosition()
        .withPositions([
          { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top' },
          { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom' },
          { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top' },
          { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom' },
        ]),
    });

    const templatePortal = new TemplatePortal(this._notificationsPanel, this.viewContainerRef);
    this._overlayRef.attach(templatePortal);

    this._overlayRef.backdropClick().subscribe(() => {
      if (this._overlayRef?.hasAttached()) {
        this._overlayRef.detach();
      }
      if (templatePortal?.isAttached) {
        templatePortal.detach();
      }
    });
  }

  protected markAllAsRead(): void {
    this.notificationsService
      .markNotificationsAsRead(this.notifications())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.loadData.next();
      });
  }

  protected markAsRead(notification: Notification): void {
    this.notificationsService
      .markNotificationAsRead(notification)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.loadData.next();
      });
  }

  protected getLinkLabel(notification: Notification): string {
    return notification.source === 'job_status' ? 'Download' : 'Open';
  }

  protected getLinkIcon(notification: Notification): string {
    return notification.source === 'job_status' ? 'download' : 'open_in_new';
  }

  protected openLink(notification: Notification): void {
    if (!notification.link) {
      return;
    }

    if (notification.useRouter) {
      this.navigateInApp(notification.link);
      return;
    }

    this.window.open(notification.link, '_blank');
  }

  protected downloadExport(job: JobStatus): void {
    const url = job.download_url || job.latest_document_url;
    if (url) {
      this.window.open(url, '_blank');
    }
  }

  protected openArchived(): void {
    this.panelView.set('archived');
  }

  protected closeArchived(): void {
    this.panelView.set('main');
  }

  protected goToFolders(): void {
    this.router.navigate(['/folders']);
  }

  protected goToCalendar(): void {
    this.router.navigate(['/calendar']);
  }

  private navigateInApp(link: string): void {
    this.router.navigateByUrl(link);
    if (this._overlayRef?.hasAttached()) {
      this._overlayRef.detach();
    }
  }
}
